import {
  max, includes, clamp, assign, find,
  cloneDeep, map, keys, indexOf, each, trim,
} from 'lodash';
import { formatMinutesAndSeconds } from '@/utils/number';
import Event from './event';
import { shallowMergeEventDetails } from './event-details-update';

export const ID = '09eb8090-68f3-4a53-838f-f79e7a6912c3';

const Periods = {
  N_A: 'N_A',
  PREMATCH: 'PREMATCH',
  IN_FIRST_HALF: 'IN_FIRST_HALF',
  AT_HALF_TIME: 'AT_HALF_TIME',
  IN_SECOND_HALF: 'IN_SECOND_HALF',
  NORMAL_TIME_COMPLETED: 'NORMAL_TIME_COMPLETED',
  IN_EXTRATIME_FIRST_HALF: 'IN_EXTRATIME_FIRST_HALF',
  AT_EXTRATIME_HALF_TIME: 'AT_EXTRATIME_HALF_TIME',
  IN_EXTRATIME_SECOND_HALF: 'IN_EXTRATIME_SECOND_HALF',
  EXTRA_TIME_COMPLETED: 'EXTRA_TIME_COMPLETED',
  IN_PENALTY_SHOOTOUTS: 'IN_PENALTY_SHOOTOUTS',
  EVENT_COMPLETED: 'EVENT_COMPLETED',
  SUSPENDED: 'SUSPENDED',
};

const mustHavePeriods = [
  Periods.IN_FIRST_HALF,
  Periods.IN_SECOND_HALF,
];

const activePeriods = [
  Periods.IN_FIRST_HALF,
  Periods.IN_SECOND_HALF,
  Periods.IN_EXTRATIME_FIRST_HALF,
  Periods.IN_EXTRATIME_SECOND_HALF,
];

const PeriodLabels = {
  TOTAL: 'Match',
  [Periods.N_A]: 'N/A',
  [Periods.PREMATCH]: 'Pregame',
  [Periods.IN_FIRST_HALF]: 'H1',
  [Periods.AT_HALF_TIME]: 'Half time',
  [Periods.IN_SECOND_HALF]: 'H2',
  [Periods.IN_EXTRATIME_FIRST_HALF]: 'ET1',
  [Periods.IN_EXTRATIME_SECOND_HALF]: 'ET2',
  [Periods.AT_EXTRATIME_HALF_TIME]: 'Break',
  [Periods.NORMAL_TIME_COMPLETED]: 'Break',
  [Periods.EXTRA_TIME_COMPLETED]: 'Break',
  [Periods.IN_PENALTY_SHOOTOUTS]: 'Penalties',
  [Periods.EVENT_COMPLETED]: 'Finished',
  [Periods.SUSPENDED]: 'Suspended',
};

export const SoccerPlayerPositions = {
  N_A: {
    position: 5,
    shortLabel: 'N/A',
    label: 'N/A',
  },
  KEEPER: {
    position: 1,
    shortLabel: 'GK',
    label: 'Keeper',
  },
  DEFENDER: {
    position: 2,
    shortLabel: 'DF',
    label: 'Defender',
  },
  MIDFIELDER: {
    position: 3,
    shortLabel: 'MF',
    label: 'Midfielder',
  },
  FORWARD: {
    position: 4,
    shortLabel: 'FW',
    label: 'Forward',
  },
};

export const getSoccerPositionShortLabel = (position) => {
  if (!position || position === 'N_A') return null;
  return SoccerPlayerPositions[position]?.shortLabel || null;
};

const PeriodAbbreviations = {
  TOTAL: 'Match',
  [Periods.N_A]: 'N/A',
  [Periods.PREMATCH]: 'Pregame',
  [Periods.IN_FIRST_HALF]: 'H1',
  [Periods.AT_HALF_TIME]: 'Half time',
  [Periods.IN_SECOND_HALF]: 'H2',
  [Periods.IN_EXTRATIME_FIRST_HALF]: 'ET1',
  [Periods.IN_EXTRATIME_SECOND_HALF]: 'ET2',
  [Periods.AT_EXTRATIME_HALF_TIME]: 'Break',
  [Periods.NORMAL_TIME_COMPLETED]: 'Break',
  [Periods.EXTRA_TIME_COMPLETED]: 'Break',
  [Periods.IN_PENALTY_SHOOTOUTS]: 'Pen',
  [Periods.EVENT_COMPLETED]: 'Finished',
  [Periods.SUSPENDED]: 'Suspended',
};

const livePeriods = [
  Periods.IN_FIRST_HALF,
  Periods.AT_HALF_TIME,
  Periods.IN_SECOND_HALF,
  Periods.NORMAL_TIME_COMPLETED,
  Periods.IN_EXTRATIME_FIRST_HALF,
  Periods.AT_EXTRATIME_HALF_TIME,
  Periods.IN_EXTRATIME_SECOND_HALF,
  Periods.EXTRA_TIME_COMPLETED,
  Periods.IN_PENALTY_SHOOTOUTS,
];

const getLivePeriods = () => [...livePeriods];

const isPrematch = (event) => Event.isPrematch(event) || includes(['N_A', 'PREMATCH'], event.period);

const isLive = (event) => Event.isLive(event) && event.period !== Periods.EVENT_COMPLETED;

const getPeriodTime = ({
  period,
  elapsedSecondInPeriod,
  halfDuration,
  extraTimeHalfDuration,
}) => {
  const elapsedMinute = parseInt((elapsedSecondInPeriod || 0) / 60, 10) + 1;
  const halfDurationInMinutes = parseInt((halfDuration || 0) / 60, 10);
  const extraTimeHalfDurationInMinutes = parseInt((extraTimeHalfDuration || 0) / 60, 10);
  switch (period) {
  case 'IN_FIRST_HALF':
    return {
      currentMinute: clamp(elapsedMinute, 1, halfDurationInMinutes),
      currentExtraMinute: max([0, elapsedMinute - halfDurationInMinutes]),
    };
  case 'IN_SECOND_HALF':
    const secondHalfOffset = halfDurationInMinutes;
    const secondHalfCurrentMinute = elapsedMinute + secondHalfOffset;

    return {
      currentMinute: clamp(secondHalfCurrentMinute, secondHalfOffset, secondHalfOffset + halfDurationInMinutes),
      currentExtraMinute: max([0, secondHalfCurrentMinute - (secondHalfOffset + halfDurationInMinutes)]),
    };
  case 'IN_EXTRATIME_FIRST_HALF':
    const firstHalfExtraOffset = halfDurationInMinutes * 2;
    const firstHalfExtraCurrentMinute = elapsedMinute + firstHalfExtraOffset;

    return {
      currentMinute: clamp(firstHalfExtraCurrentMinute, firstHalfExtraOffset + extraTimeHalfDurationInMinutes),
      currentExtraMinute: max([0, firstHalfExtraCurrentMinute - (firstHalfExtraOffset + extraTimeHalfDurationInMinutes)]),
    };
  case 'IN_EXTRATIME_SECOND_HALF':
    const secondHalfExtraOffset = (halfDurationInMinutes * 2) + extraTimeHalfDurationInMinutes;
    const secondHalfExtraCurrentMinute = elapsedMinute + secondHalfExtraOffset;

    return {
      currentMinute: clamp(secondHalfExtraCurrentMinute, secondHalfExtraOffset, secondHalfExtraOffset + extraTimeHalfDurationInMinutes),
      currentExtraMinute: max([0, secondHalfExtraCurrentMinute - (secondHalfExtraOffset + extraTimeHalfDurationInMinutes)]),
    };
  default:
    return undefined;
  }
};

const getPeriodLabel = ({
  period,
  elapsedSecondInPeriod,
  halfDuration,
  extraTimeHalfDuration,
}) => {
  const label = PeriodLabels[period] || '';
  if (!includes(activePeriods, period)) return label;

  const time = getPeriodTime({
    period,
    elapsedSecondInPeriod,
    halfDuration,
    extraTimeHalfDuration,
  });

  const labelBase = `${label} · ${time.currentMinute}`;
  const labelAddon = `+${time.currentExtraMinute}`;
  return `${labelBase}${time.currentExtraMinute ? labelAddon : ''}'`;
};

const getPeriodValue = (periodCode) => {
  if (periodCode === 'TOTAL') return 0;
  return indexOf(keys(Periods), periodCode) + 1;
};

const getDetails = (event, prematch) => {
  const scorePerPeriod = map(
    Event.getAvailablePeriods({
      allPeriods: keys(Periods),
      scorePerPeriod: event.home.scorePerPeriod,
      mustHavePeriods: [
        Periods.IN_FIRST_HALF,
        Periods.IN_SECOND_HALF,
      ],
    }),
    (period) => {
      const isVisible = !prematch && getPeriodValue(event.period) >= getPeriodValue(period);
      return {
        code: period,
        label: PeriodAbbreviations[period],
        homeValue: isVisible ? event.home.scorePerPeriod[period] || 0 : '',
        awayValue: isVisible ? event.away.scorePerPeriod[period] || 0 : '',
      };
    },
  );

  return [
    {
      code: 'TOTAL',
      label: PeriodAbbreviations.TOTAL,
      homeValue: prematch ? '-' : event.home.score,
      awayValue: prematch ? '-' : event.away.score,
    },
    ...scorePerPeriod,
  ];
};

const updateEvent = (eventDetails, payload) => shallowMergeEventDetails(eventDetails, {
      scorePerPeriod: payload.state.scorePerPeriod,
      homeScore: payload.state.homeScore,
      awayScore: payload.state.awayScore,
      homeScoreRegularTime: payload.state.homeScoreRegularTime,
      awayScoreRegularTime: payload.state.awayScoreRegularTime,
      homeScorePenaltyShootout: payload.state.homeScorePenaltyShootout,
      awayScorePenaltyShootout: payload.state.awayScorePenaltyShootout,
      homeScoreFirstLeg: payload.state.homeScoreFirstLeg,
      awayScoreFirstLeg: payload.state.awayScoreFirstLeg,
      yellowCardsInMatch: payload.state.yellowCardsInMatch,
      redCardsInMatch: payload.state.redCardsInMatch,
      cornersInMatch: payload.state.cornersInMatch,
      foulsInMatch: payload.state.foulsInMatch,
      shotsOnTargetInMatch: payload.state.shotsOnTargetInMatch,
      shotsOffTargetInMatch: payload.state.shotsOffTargetInMatch,
      freeKicksInMatch: payload.state.freeKicksInMatch,
      penaltiesInMatch: payload.state.penaltiesInMatch,
      attacksInMatch: payload.state.attacksInMatch,
      dangerousAttacksInMatch: payload.state.dangerousAttacksInMatch,
      yellowCardsPerPeriod: payload.state.yellowCardsPerPeriod,
      redCardsPerPeriod: payload.state.redCardsPerPeriod,
      cornersPerPeriod: payload.state.cornersPerPeriod,
      foulsPerPeriod: payload.state.foulsPerPeriod,
      shotsOnTargetPerPeriod: payload.state.shotsOnTargetPerPeriod,
      shotsOffTargetPerPeriod: payload.state.shotsOffTargetPerPeriod,
      freeKicksPerPeriod: payload.state.freeKicksPerPeriod,
      penaltiesPerPeriod: payload.state.penaltiesPerPeriod,
      attacksPerPeriod: payload.state.attacksPerPeriod,
      dangerousAttacksPerPeriod: payload.state.dangerousAttacksPerPeriod,
      period: payload.state.period,
      elapsedSecondInPeriod: payload.state.elapsedSecondInPeriod,
      homeLineup: payload.competitors?.homeTeam?.lineup,
      awayLineup: payload.competitors?.awayTeam?.lineup,
      eventInBreak: payload.state.eventInBreak,
      eventPreMatch: payload.state.eventPreMatch,
      actions: payload.state.actions,
      riskFlags: payload.state.riskFlags,
      goalsFromPenaltyInMatch: payload.state.goalsFromPenaltyInMatch,
      offsidesInMatch: payload.state.offsidesInMatch,
      assistsInMatch: payload.state.assistsInMatch,
      accuratePassesInMatch: payload.state.accuratePassesInMatch,
      totalPassesInMatch: payload.state.totalPassesInMatch,
      accurateCrossesInMatch: payload.state.accurateCrossesInMatch,
      totalCrossesInMatch: payload.state.totalCrossesInMatch,
      tacklesInMatch: payload.state.tacklesInMatch,
      interceptionsInMatch: payload.state.interceptionsInMatch,
      savesInMatch: payload.state.savesInMatch,
      ownGoalsInMatch: payload.state.ownGoalsInMatch,
      goalsFromPenaltyPerPeriod: payload.state.goalsFromPenaltyPerPeriod,
      offsidesPerPeriod: payload.state.offsidesPerPeriod,
      ownGoalsPerPeriod: payload.state.ownGoalsPerPeriod,
      hydrationBreakInProgress: payload.state.hydrationBreakInProgress,
      hydrationBreakStartTime: payload.state.hydrationBreakStartTime,
});

const formatPeriodTime = ({
  period,
  elapsedSecondInPeriod,
  halfDuration,
  extraTimeHalfDuration,
}) => {
  const time = getPeriodTime({
    period,
    elapsedSecondInPeriod,
    halfDuration,
    extraTimeHalfDuration,
  });
  if (!time) return '';
  const secondsLeftInPeriod = (time.currentMinute + time.currentExtraMinute) * 60;
  return formatMinutesAndSeconds(secondsLeftInPeriod);
};

const formatPeriodMinute = ({
  period,
  elapsedSecondInPeriod,
  halfDuration,
  extraTimeHalfDuration,
}) => {
  const time = getPeriodTime({
    period,
    elapsedSecondInPeriod,
    halfDuration,
    extraTimeHalfDuration,
  });
  if (!time) return '';
  const labelBase = `${time.currentMinute}`;
  const labelAddon = `+${time.currentExtraMinute}`;
  return `${labelBase}${time.currentExtraMinute ? labelAddon : ''}'`;
};

export const createPlayerStatisticsColumns = () => ({
  position: {
    label: 'P',
    tooltip: 'Position',
    field: 'position',
    style: {
      'text-align': 'right',
    },
  },
  goals: {
    label: 'G',
    tooltip: 'Goals',
    field: 'stats.goals',
    style: {
      'text-align': 'center',
    },
  },
  goalsFromPenalty: {
    label: 'PG',
    tooltip: 'Goals from Penalty',
    field: 'stats.goalsFromPenalty',
    style: {
      'text-align': 'center',
    },
  },
  ownGoals: {
    label: 'OG',
    tooltip: 'Own Goals',
    field: 'stats.ownGoals',
    style: {
      'text-align': 'center',
    },
  },
  yellowCards: {
    label: 'YC',
    tooltip: 'Yellow Cards',
    field: 'stats.yellowCards',
    style: {
      'text-align': 'center',
    },
  },
  redCards: {
    label: 'RC',
    tooltip: 'Red Cards',
    field: 'stats.redCards',
    style: {
      'text-align': 'center',
    },
  },
  offsides: {
    label: 'OFF',
    tooltip: 'Offsides',
    field: 'stats.offsides',
    style: {
      'text-align': 'center',
    },
  },
  assists: {
    label: 'A',
    tooltip: 'Assists',
    field: 'stats.assists',
    style: {
      'text-align': 'center',
    },
  },
  accuratePasses: {
    label: 'AP',
    tooltip: 'Accurate Passes',
    field: 'stats.accuratePasses',
    style: {
      'text-align': 'center',
    },
  },
  totalPasses: {
    label: 'TP',
    tooltip: 'Total Passes',
    field: 'stats.totalPasses',
    style: {
      'text-align': 'center',
    },
  },
  accurateCrosses: {
    label: 'AC',
    tooltip: 'Accurate Crosses',
    field: 'stats.accurateCrosses',
    style: {
      'text-align': 'center',
    },
  },
  totalCrosses: {
    label: 'TC',
    tooltip: 'Total Crosses',
    field: 'stats.totalCrosses',
    style: {
      'text-align': 'center',
    },
  },
  tackles: {
    label: 'T',
    tooltip: 'Tackles',
    field: 'stats.tackles',
    style: {
      'text-align': 'center',
    },
  },
  interceptions: {
    label: 'I',
    tooltip: 'Interceptions',
    field: 'stats.interceptions',
    style: {
      'text-align': 'center',
    },
  },
  saves: {
    label: 'S',
    tooltip: 'Saves',
    field: 'stats.saves',
    style: {
      'text-align': 'center',
    },
  },
  totalShots: {
    label: 'TS',
    tooltip: 'Total Shots',
    field: 'stats.totalShots',
    style: {
      'text-align': 'center',
    },
  },
  totalShotsOnTarget: {
    label: 'TSOT',
    tooltip: 'Total Shots on Target',
    field: 'stats.totalShotsOnTarget',
    style: {
      'text-align': 'center',
    },
  },
});

const formatPeriodLabel = ({ period }) => PeriodLabels[period] ?? period;

const formatPeriodAbbreviation = ({ period }) => PeriodAbbreviations[period] ?? period;

const formatPeriod = (details, options) => {
  const formatLabel = options?.formatLabel ?? formatPeriodLabel;
  const formatTime = options?.formatTime ?? formatPeriodMinute;
  const separator = options?.separator ?? '·';

  const label = formatLabel(details);
  const time = formatTime(details);

  return (includes(activePeriods, details.period) ? `${label} ${separator} ${time}` : label);
};

const calculatePeriodProgress = ({
  period,
  elapsedSecondInPeriod,
  halfDuration,
  extraTimeHalfDuration,
}) => {
  if (!includes(activePeriods, period)) return -1;
  if (includes(PeriodAbbreviations[period], 'ET')) return (elapsedSecondInPeriod / extraTimeHalfDuration) * 100;
  return (elapsedSecondInPeriod / halfDuration) * 100;
};

const statisticTypes = {
  GOALS: 'GOALS',
  SHOTS_ON_TARGET: 'SHOTS_ON_TARGET',
  SHOTS_OFF_TARGET: 'SHOTS_OFF_TARGET',
  /* ATTACKS: 'ATTACKS', */
  DANGEROUS_ATTACKS: 'DANGEROUS_ATTACKS',
  CORNERS: 'CORNERS',
  FREEKICKS: 'FREEKICKS',
  PENALTIES: 'PENALTIES',
  FOULS: 'FOULS',
  YELLOW_CARDS: 'YELLOW_CARDS',
  RED_CARDS: 'RED_CARDS',
  OWN_GOALS: 'OWN_GOALS',
  GOALS_FROM_PENALTY: 'GOALS_FROM_PENALTY',
  OFFSIDES: 'OFFSIDES',
  ASSISTS: 'ASSISTS',
  ACCURATE_PASSES: 'ACCURATE_PASSES',
  TOTAL_PASSES: 'TOTAL_PASSES',
  ACCURATE_CROSSES: 'ACCURATE_CROSSES',
  TOTAL_CROSSES: 'TOTAL_CROSSES',
  TACKLES: 'TACKLES',
  INTERCEPTIONS: 'INTERCEPTIONS',
  SAVES: 'SAVES',
};

const createStatisticsConfiguration = (event) => ({
  periods: Event.getAvailablePeriods({
    allPeriods: keys(Periods),
    scorePerPeriod: event.home.scorePerPeriod,
    mustHavePeriods: [
      Periods.IN_FIRST_HALF,
      Periods.IN_SECOND_HALF,
    ],
  }),
  fields: {
    [statisticTypes.GOALS]: 'scorePerPeriod',
    [statisticTypes.SHOTS_ON_TARGET]: 'shotsOnTargetPerPeriod',
    [statisticTypes.SHOTS_OFF_TARGET]: 'shotsOffTargetPerPeriod',
    /* [statisticTypes.ATTACKS]: 'attacksPerPeriod', */
    [statisticTypes.DANGEROUS_ATTACKS]: 'dangerousAttacksPerPeriod',
    [statisticTypes.CORNERS]: 'cornersPerPeriod',
    [statisticTypes.FREEKICKS]: 'freeKicksPerPeriod',
    [statisticTypes.PENALTIES]: 'penaltiesPerPeriod',
    [statisticTypes.FOULS]: 'foulsPerPeriod',
    [statisticTypes.YELLOW_CARDS]: 'yellowCardsPerPeriod',
    [statisticTypes.RED_CARDS]: 'redCardsPerPeriod',
    [statisticTypes.OWN_GOALS]: 'ownGoalsPerPeriod',
    [statisticTypes.GOALS_FROM_PENALTY]: 'goalsFromPenaltyPerPeriod',
    [statisticTypes.OFFSIDES]: 'offsidesPerPeriod',
  },
  labels: {
    [statisticTypes.GOALS]: 'Goals',
    [statisticTypes.SHOTS_ON_TARGET]: 'Shots on target',
    [statisticTypes.SHOTS_OFF_TARGET]: 'Shots off target',
    /* [statisticTypes.ATTACKS]: 'Attacks', */
    [statisticTypes.DANGEROUS_ATTACKS]: 'Dangerous attacks',
    [statisticTypes.CORNERS]: 'Corners',
    [statisticTypes.FREEKICKS]: 'Free kicks',
    [statisticTypes.PENALTIES]: 'Penalties',
    [statisticTypes.FOULS]: 'Fouls',
    [statisticTypes.YELLOW_CARDS]: 'Yellow cards',
    [statisticTypes.RED_CARDS]: 'Red cards',
    [statisticTypes.OWN_GOALS]: 'Own goals',
    [statisticTypes.GOALS_FROM_PENALTY]: 'Goals from penalty',
    [statisticTypes.OFFSIDES]: 'Offsides',
    [statisticTypes.ASSISTS]: 'Assists',
    [statisticTypes.ACCURATE_PASSES]: 'Accurate passes',
    [statisticTypes.TOTAL_PASSES]: 'Total passes',
    [statisticTypes.ACCURATE_CROSSES]: 'Accurate crosses',
    [statisticTypes.TOTAL_CROSSES]: 'Total crosses',
    [statisticTypes.TACKLES]: 'Tackles',
    [statisticTypes.INTERCEPTIONS]: 'Interceptions',
    [statisticTypes.SAVES]: 'Saves',
  },
  totalFields: {
    [statisticTypes.GOALS]: 'score',
    [statisticTypes.SHOTS_ON_TARGET]: 'shotsOnTarget',
    [statisticTypes.SHOTS_OFF_TARGET]: 'shotsOffTarget',
    /* [statisticTypes.ATTACKS]: 'attacks', */
    [statisticTypes.DANGEROUS_ATTACKS]: 'dangerousAttacks',
    [statisticTypes.CORNERS]: 'corners',
    [statisticTypes.FREEKICKS]: 'freeKicks',
    [statisticTypes.PENALTIES]: 'penalties',
    [statisticTypes.FOULS]: 'fouls',
    [statisticTypes.YELLOW_CARDS]: 'yellowCards',
    [statisticTypes.RED_CARDS]: 'redCards',
    [statisticTypes.OWN_GOALS]: 'ownGoals',
    [statisticTypes.GOALS_FROM_PENALTY]: 'goalsFromPenalty',
    [statisticTypes.OFFSIDES]: 'offsides',
    [statisticTypes.ASSISTS]: 'assists',
    [statisticTypes.ACCURATE_PASSES]: 'accuratePasses',
    [statisticTypes.TOTAL_PASSES]: 'totalPasses',
    [statisticTypes.ACCURATE_CROSSES]: 'accurateCrosses',
    [statisticTypes.TOTAL_CROSSES]: 'totalCrosses',
    [statisticTypes.TACKLES]: 'tackles',
    [statisticTypes.INTERCEPTIONS]: 'interceptions',
    [statisticTypes.SAVES]: 'saves',
  },
  formatters: {},
  transform({ period, value }) {
    if (period === 'TOTAL') return value ?? NaN;
    return value?.[period] ?? NaN;
  },
  // old soccer statistics format
  /* transform({
    period,
    field,
    data,
    value,
  }) {
    let entries = value;
    if (isArray(field)) entries = reduce(field, (combined, key) => combined.concat(data[key]), []);
    entries = filter(entries, { period });
    return entries.length;
  }, */
});

const getAvailablePeriods = (event) => [
  {
    id: 'TOTAL',
    label: 'Full event',
  },
  ...map(
    Event.getAvailablePeriods({
      allPeriods: keys(Periods),
      scorePerPeriod: event.home.scorePerPeriod,
      mustHavePeriods: [
        Periods.IN_FIRST_HALF,
        Periods.IN_SECOND_HALF,
      ],
    }),
    (period) => ({
      id: period,
      label: PeriodLabels[period],
    }),
  ),
];

const soccerTimelineActionTypes = [
  {
    type: 'AWAY_GOAL',
    message: '{playerIndex1}',
  },
  {
    type: 'AWAY_GOAL_OWN_GOAL',
    message: '{playerIndex1} (og)',
  },
  {
    type: 'AWAY_GOAL_FROM_PENALTY',
    message: '{playerIndex1} (pen)',
  },
  {
    type: 'AWAY_YELLOW_CARD',
    message: '{playerIndex1}',
  },
  {
    type: 'AWAY_SECOND_YELLOW_CARD',
    message: '{playerIndex1}',
  },
  {
    type: 'AWAY_RED_CARD',
    message: '{playerIndex1}',
  },
  {
    type: 'AWAY_SUBSTITUTION',
    message: '({playerIndex1}) {playerIndex2}',
  },
  {
    type: 'AWAY_PENALTY_MISS',
    message: '{playerIndex1} missed pen',
  },
  {
    type: 'HOME_GOAL',
    message: '{playerIndex1}',
  },
  {
    type: 'HOME_GOAL_OWN_GOAL',
    message: '(og) {playerIndex1}',
  },
  {
    type: 'HOME_GOAL_FROM_PENALTY',
    message: '(pen) {playerIndex1}',
  },
  {
    type: 'HOME_YELLOW_CARD',
    message: '{playerIndex1}',
  },
  {
    type: 'HOME_SECOND_YELLOW_CARD',
    message: '{playerIndex1}',
  },
  {
    type: 'HOME_RED_CARD',
    message: '{playerIndex1}',
  },
  {
    type: 'HOME_SUBSTITUTION',
    message: '{playerIndex2} ({playerIndex1})',
  },
  {
    type: 'HOME_PENALTY_MISS',
    message: '{playerIndex1} missed pen',
  },
];

const getDetailedScoreboard = ({
  actions, halfDuration, extraTimeHalfDuration, squad,
}) => {
  const timeline = [];
  if (!Array.isArray(actions)) return timeline;

  each(actions, (action) => {
    if (!action || !action.type) return;
    const typeObj = find(soccerTimelineActionTypes, (t) => t.type === action.type);
    if (!typeObj) return;

    const isHome = action.type.startsWith('HOME_');
    let side = isHome ? 'left' : 'right';
    if (action.type === 'AWAY_GOAL_OWN_GOAL') side = 'right';
    if (action.type === 'HOME_GOAL_OWN_GOAL') side = 'left';

    // Helper to get playerId or playerIndex or 'N/A'
    const getPlayerValue = (lineup, playerIndex) => {
      if (!lineup?.length) return 'N/A';
      const player = find(lineup, (p) => p.playerIndex === playerIndex);
      if (!player) return playerIndex || 'N/A';
      const playerName = player.personalInfo?.name;
      const mappedPlayerName = playerName ? `${trim(playerName.split(',')[1]).charAt(0).toUpperCase()}. ${playerName.split(',')[0]}` : 'N/A';
      return player.shortName || mappedPlayerName || player.playerIndex || player.playerId || 'N/A';
    };

    // Prepare replacements
    const replacements = {
      playerIndex1: getPlayerValue(squad, action.playerIndex1),
      playerIndex2: getPlayerValue(squad, action.playerIndex2),
    };

    // Replace placeholders in message
    let { message } = typeObj;
    Object.keys(replacements).forEach((key) => {
      message = message.replace(new RegExp(`{${key}}`, 'g'), replacements[key]);
    });

    const timeOfAction = getPeriodLabel({
      period: action.period,
      elapsedSecondInPeriod: action.elapsedSecondInPeriod,
      halfDuration,
      extraTimeHalfDuration,
    });

    timeline.push({
      time: timeOfAction,
      team: action.team,
      type: action.type,
      side,
      message,
    });
  });

  return timeline;
};

export default {
  ID,
  Periods,
  mustHavePeriods,
  activePeriods,
  SoccerPlayerPositions,
  getSoccerPositionShortLabel,
  PeriodAbbreviations,
  PeriodLabels,
  isPrematch,
  isLive,
  getPeriodTime,
  getPeriodLabel,
  getDetails,
  updateEvent,
  formatPeriodTime,
  formatPeriodMinute,
  formatPeriodLabel,
  formatPeriodAbbreviation,
  formatPeriod,
  calculatePeriodProgress,
  createStatisticsConfiguration,
  getAvailablePeriods,
  getLivePeriods,
  getDetailedScoreboard,
  createPlayerStatisticsColumns,
};
